import React, { useState } from 'react';
import { AuthLayout } from './AuthLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { useAuth } from './AuthContext';
import { Eye, EyeOff, Mail, Lock, Loader2, AlertCircle, Activity } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog'; 
import { toast } from 'sonner';

interface SignInProps {
  onResetPassword: () => void;
}

export const SignIn: React.FC<SignInProps> = ({ onResetPassword }) => {
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await signIn(formData.email, formData.password);
      if (!result.success) {
        setError(result.error || 'Sign in failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleResetPasswordRequest = async () => {
    setIsResetting(true);
    try {
     const response = await fetch(
       `${import.meta.env.VITE_API_URL}auth/password/reset`,
       {
         method: "POST",
         headers: { "Content-Type": "application/json" },
         body: JSON.stringify({ email: resetEmail }),
       }
      );
      const data = await response.json();
      if(!response.ok) throw new Error(data.message || "Request failed");
      setShowResetDialog(false);
      setResetEmail("");
      toast.success('Password reset email sent!', {
        description: 'Please check your inbox to continue.',
      });
    } catch (err:any) {
      toast.error('Failed to send reset email', {
        description: err.message || 'Please try again later.',
      });
    } finally {
      setIsResetting(false);
    }
  };

  const isFormValid = formData.email && formData.password;

    if (isSubmitting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4 animate-pulse">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <p className="text-base text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout title="Sign In" subtitle="Enter your credentials to access your account">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="destructive" className='bg-red-50 border-red-200'>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription className='text-red-600'>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="pl-10"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="pl-10 pr-10"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-end">
              <button
               type="button"
               onClick={() => setShowResetDialog(true)}
               className="text-blue-600 hover:text-blue-700 hover:underline text-xs"
               disabled={isResetting}
               >
                Forgot password?
              </button>
           </div>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={!isFormValid || isSubmitting}
          >          
           Sign In
          </Button>
      </form>
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
       <DialogContent>
        <DialogHeader>
          <DialogTitle>Forgot Password</DialogTitle>
          <DialogDescription>
             Enter your registered email and we’ll send you reset instructions.
          </DialogDescription>
        </DialogHeader>
        {/* Email Input */}
        <div className="space-y-2">
          <Label>Email Address</Label>
            <Input
              type="email"
              placeholder="Enter your email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
         </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>{
                 setShowResetDialog(false);
                 setResetEmail("");
              }}
              disabled={isResetting}
            >
             Cancel
            </Button>
            <Button onClick={handleResetPasswordRequest}
            disabled={!resetEmail || isResetting}>
             Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthLayout>
  );
};